'use client'

import HeaderWithBackButton from '@/components/common/HeaderWithBackButton'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TypeModal from '@/app/contact/_components/TypeModal'
import { GA_EVENTS } from '@/utils/analytics'
import { Camera, Xmark } from '@peacecover/icon'
import Image from 'next/image'
import { Button } from '@peacecover/component'
import { isValidEmail } from '@/utils/validateEmail'
import { postProposal } from '@/utils/postProposal'

const Contact = () => {
  const { t } = useTranslation('contact')

  const [email, setEmail] = useState<string>('')
  const [content, setContent] = useState<string>('')
  const [type, setType] = useState<string>(t('none'))
  const [image, setImage] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!image) {
      setPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(image)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [image])

  const isDisabled = email.trim().length === 0 || content.trim().length === 0 || type === t('none')

  const handleSubmit = async () => {
    if (!isValidEmail(email)) {
      alert(t('contact_email_error'))
      return
    }

    try {
      const response = await postProposal(email, content, type, image)
      if (response.ok) {
        alert('문의가 접수되었습니다.')
        setEmail('')
        setContent('')
        setType(t('none'))
        setImage(null)
        setPreviewUrl(null)
      } else {
        throw new Error('Failed to submit proposal')
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    GA_EVENTS.CONTACT_START()
  }, [])

  return (
    <section>
      <div className="sm:hidden">
        <HeaderWithBackButton />
      </div>
      <div className="mx-auto mt-10 sm:mt-[140px] sm:max-w-[1002px]">
        <div className="flex flex-col justify-center items-center">
          <h2 className="font-pretendard-bold text-[26px] text-main-light-1 dark:text-main-dark-1 leading-tight">
            {t('contact_header')}
          </h2>
          <p className="text-[15px] text-main-light-4 dark:text-main-dark-4 mt-3 mb-10 leading-tight">
            {t('contact_message')}
          </p>
        </div>
        <form className="flex flex-col">
          <TypeModal type={type} setType={setType} />
          <div className="mx-5">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('contact_email')}
              className="w-full bg-secondary font-pretendard-bold text-[17px] text-main-light-2 dark:text-main-dark-2 rounded-[12px] py-4 px-5 mt-[14px] mb-[14px] outline-none"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('contact_content')}
              className="resize-none w-full bg-secondary font-pretendard-bold text-[17px] text-main-light-2 dark:text-main-dark-2 rounded-[12px] py-4 px-5 outline-none"
              rows={5}
            />
            <div className='mt-[26px]'>
              <p className='text-[17px] text-text-main-lv2'>{t('contact_attach_image')}</p>
              <input
                ref={fileInputRef}
                type="file"
                className='hidden'
                onChange={(e) => setImage(e.target.files?.[0] ?? null)}
                accept="image/*"
              />
              <div className='flex items-start gap-[12px] pt-[20px]'>
                <div
                  className='w-[75px] h-[98px] rounded-[12px] border border-separator1 flex flex-col gap-[2px] items-center justify-center cursor-pointer'
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
                  }}
                >
                  <Camera size={24} className='text-text-main-lv1' />
                  <p className='text-[12px] text-text-sub-lv1'>{image ? 1 : 0}/1</p>
                </div>
                {previewUrl && (
                  <div className='relative'>
                    <div className='w-[75px] h-[98px] rounded-[12px] border border-separator1 overflow-hidden'>
                      <Image src={previewUrl} alt="contact preview" fill sizes="75px" className='object-cover rounded-[12px]' />
                    </div>
                    <button
                      type="button"
                      className='absolute top-[-10px] right-[-10px] bg-primary rounded-full p-[5px] z-10'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setImage(null)
                      }}>
                      <Xmark size={14} className='text-on-primary' />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <Button
              type='button'
              className='mt-[75px] w-full'
              size='large'
              disabled={isDisabled}
              onClick={handleSubmit}
            >
              {t('contact_submit')}
            </Button>
          </div>
        </form>
      </div>
    </section>
  )
}

export default Contact
